import React, { useId } from 'react';

import { getIllustration, ViewBoxSize } from './utils';
import { AnalyticsId } from '../../constants';

export type IllustrationColor = 'neutral' | 'cherry' | 'blueberry';

export interface BaseSvgIllustrationProps {
  color: IllustrationColor;
  svgProperties: React.SVGProps<SVGSVGElement>;
  title: React.ReactNode;
}
export interface SvgIllustrationProps extends BaseSvgIllustrationProps {
  size: number;
}

export type SvgIllustration = React.ComponentType<SvgIllustrationProps>;

export interface BaseIllustrationProps {
  /* aria-label for the <svg> element. Used as <title> tag. */
  ariaLabel?: string;
  /* Changes the size of the illustration. */
  size?: number;
  /* Changes the color of the illustration. */
  color?: IllustrationColor;
  /* Adds custom classes to the element. */
  className?: string;
  /** Sets the data-testid attribute. */
  testId?: string;
}

export interface IllustrationProps extends BaseIllustrationProps {
  /* Sets which illustration should be displayed. */
  illustration: SvgIllustration;
  /** Ref passed to the component */
  ref?: React.Ref<SVGSVGElement | null>;
}

export const Illustration: React.FC<IllustrationProps> = props => {
  const { illustration, ariaLabel, className = '', size = 512, color = 'neutral', testId, ref, ...other } = props;

  const titleId = useId();
  const viewBox = getIllustration({ size, medium: ViewBoxSize.Medium, small: ViewBoxSize.Small });

  const svgProperties = {
    'data-testid': testId,
    'data-analyticsid': AnalyticsId.Illustration,
    ref,
    className,
    role: ariaLabel ? 'img' : 'presentation',
    'aria-labelledby': ariaLabel ? titleId : undefined,
    focusable: false,
    'aria-hidden': ariaLabel ? undefined : true,
    viewBox,
    style: { minWidth: size, minHeight: size },
    width: size,
    height: size,
    fill: color,
    ...other,
  };

  const svgElement = React.createElement(illustration, {
    size,
    color,
    svgProperties: svgProperties,
    title: ariaLabel && <title id={titleId}>{ariaLabel}</title>,
  });

  return svgElement;
};

Illustration.displayName = 'Illustration';

export default Illustration;
